import { getDocument } from '@atlaskit/browser-apis';

export type RetryWait = {
	wait: (retries: number) => Promise<void>;
	/**
	 * Ends the current wait. Returns true when a wait was running so callers can skip
	 * `terminate()` — graphql-ws emits 4499 from terminate during `retryWait` without ending it.
	 */
	abort: () => boolean;
};

/** Capped exponential backoff with the same jitter shape as graphql-ws. */
export const createRetryWait = (maxRetryWait: number): RetryWait => {
	let abortCurrent: (() => void) | undefined;

	return {
		abort: () => {
			if (!abortCurrent) {
				return false;
			}
			abortCurrent();
			abortCurrent = undefined;
			return true;
		},
		wait: async (retries: number): Promise<void> => {
			const jitter = Math.floor(Math.random() * 2700 + 300);
			const delay = Math.min(1000 * 2 ** retries + jitter, maxRetryWait);
			await new Promise<void>((resolve) => {
				const timer = setTimeout(() => {
					abortCurrent = undefined;
					resolve();
				}, delay);
				abortCurrent = () => {
					clearTimeout(timer);
					resolve();
				};
			});
		},
	};
};

// The library's own default `shouldRetry`. From 5.16, `Client.terminate()` emits a close-like
// `Error`, so the close shape must be checked before the type.
const isLikeCloseEvent = (value: unknown): value is { code: unknown; reason: unknown } =>
	typeof value === 'object' && value !== null && 'code' in value && 'reason' in value;

/**
 * graphql-ws retries closes by default, but stops on bare `socket.onerror` events.
 * Retry non-Error failures too so a failed socket can reconnect.
 */
export const shouldRetryTransportFailure = (errOrCloseEvent: unknown): boolean =>
	isLikeCloseEvent(errOrCloseEvent) || !(errOrCloseEvent instanceof Error);

/**
 * Aborts an in-flight backoff, or calls terminate(), on online/visible when disconnected;
 * always on bfcache restore and resume.
 */
export const addRecoveryListeners = ({
	terminate,
	isConnected,
	abortRetryWait,
}: {
	terminate: () => void;
	isConnected: () => boolean;
	abortRetryWait: () => boolean;
}): (() => void) => {
	const doc = getDocument();

	const reconnect = (): void => {
		if (abortRetryWait()) {
			return;
		}
		terminate();
	};

	const wakeIfDisconnected = (): void => {
		if (!isConnected()) {
			reconnect();
		}
	};

	const onVisibilityChange = (): void => {
		if (doc?.visibilityState === 'visible') {
			wakeIfDisconnected();
		}
	};

	const onPageShow = (event: PageTransitionEvent): void => {
		// pageshow also fires on ordinary loads; only `persisted` is bfcache.
		if (event.persisted) {
			reconnect();
		}
	};

	const onResume = (): void => {
		reconnect();
	};

	window.addEventListener('online', wakeIfDisconnected);
	window.addEventListener('pageshow', onPageShow);
	doc?.addEventListener('visibilitychange', onVisibilityChange);
	// Chrome tab freeze; no pageshow.
	doc?.addEventListener('resume', onResume);

	return () => {
		window.removeEventListener('online', wakeIfDisconnected);
		window.removeEventListener('pageshow', onPageShow);
		doc?.removeEventListener('visibilitychange', onVisibilityChange);
		doc?.removeEventListener('resume', onResume);
	};
};
